import type { PatternContent } from './types'

export const CREATIONAL_AND_EARLY_STRUCTURAL: PatternContent[] = [
  {
    slug: 'factory-method',
    teach: [
      {
        label: 'The problem',
        text: 'Your code says new RedisStore() all over, so adding a second store means editing every caller. The caller only needs something that stores.',
      },
      {
        label: 'The shape',
        text: 'Put the choice in one method that returns the interface. Callers ask for a store; the method (or a subclass of it) picks the concrete one.',
      },
      {
        label: 'A trap',
        text: 'A method with a switch that returns objects is not automatically a pattern worth naming. The point is callers never see the concrete type.',
      },
    ],
    whenToUse:
      'Callers should depend on an interface while the concrete type depends on config, environment or a subclass.',
    whenNotTo:
      'There is only one concrete type and no sign of a second. A plain constructor call is clearer than a method that wraps it.',
    quizzes: [
      {
        format: 'judge',
        prompt:
          'A method that returns a Cache interface, choosing Redis or file by config, is a Factory Method.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'The creating method hides the concrete class; callers only hold the interface.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does Factory Method let a caller stop doing?',
        choices: [
          { label: 'Naming the concrete class', correct: true },
          { label: 'Copying existing objects' },
          { label: 'Sharing one instance' },
          { label: 'Wrapping a foreign API' },
        ],
        explanation: 'The caller asks for the interface; the factory method decides the class.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class CacheManager extends Manager
{
    public function createDriver($name)
    {
        return match ($name) {
            'redis' => $this->makeRedis(),
            'file' => $this->makeFile(),
        };
    }
}`,
        choices: [
          { label: 'Factory Method', correct: true },
          { label: 'Builder' },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'One method picks the concrete driver and returns it as the shared driver type.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `func NewStore(kind string) Store {
	switch kind {
	case "redis":
		return &RedisStore{}
	default:
		return &FileStore{}
	}
}

store := NewStore("redis")
v := store.(*RedisStore).Addr`,
        choices: [
          { label: 'Abstract Factory' },
          { label: 'Factory Method' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation: 'The caller casts back to *RedisStore, so the factory hides nothing.',
      },
    ],
  },
  {
    slug: 'abstract-factory',
    teach: [
      {
        label: 'The problem',
        text: 'A mysql grammar must pair with a mysql processor. If callers pick the parts one by one, someone mixes a Postgres grammar with a MySQL one.',
      },
      {
        label: 'The shape',
        text: 'One factory interface with a method per part: grammar(), processor(), connection(). Each concrete factory returns a matching set.',
      },
      {
        label: 'A trap',
        text: 'Adding a new kind of part means changing every factory. If the parts are not truly a family, a single Factory Method is enough.',
      },
    ],
    whenToUse:
      'Several objects must be swapped together as a set and must never be mixed across variants, such as per-driver database parts.',
    whenNotTo:
      'You build only one kind of object, or the parts can vary independently. Then one factory method per object is lighter.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'A class with one method that returns one cache store is an Abstract Factory.',
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'One product is a Factory Method; Abstract Factory needs a family of matching products.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does Abstract Factory guarantee that separate factories do not?',
        choices: [
          { label: 'Parts all come from one variant', correct: true },
          { label: 'Only one instance exists' },
          { label: 'Objects are built step by step' },
          { label: 'Objects are cloned cheaply' },
        ],
        explanation: 'Each factory returns parts that belong together, so variants never get mixed.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `type DriverKit interface {
	Grammar() Grammar
	Proc() Proc
	Conn() Conn
}

type Pg struct{ d string }

func (Pg) Grammar() Grammar { return PgG{} }
func (Pg) Proc() Proc { return PgP{} }
func (k Pg) Conn() Conn { return dial(k.d) }`,
        choices: [
          { label: 'Builder' },
          { label: 'None of these' },
          { label: 'Abstract Factory', correct: true },
          { label: 'Misused' },
        ],
        explanation: 'One kit returns a matching grammar, processor and connection for a single driver.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class MailKit
{
    public function mailer(): Mailer
    {
        return new SmtpMailer();
    }

    public function logger(): Logger
    {
        return new FileLogger();
    }
}`,
        choices: [
          { label: 'Abstract Factory' },
          { label: 'Factory Method' },
          { label: 'Misused' },
          { label: 'None of these', correct: true },
        ],
        explanation:
          'No interface and no variants: just a bag of unrelated news, not a family of matching parts.',
      },
    ],
  },
  {
    slug: 'builder',
    teach: [
      {
        label: 'The problem',
        text: 'A constructor with nine arguments, six of them optional, is unreadable at the call site. Nobody remembers which null means what.',
      },
      {
        label: 'The shape',
        text: 'A builder collects settings one call at a time, then build() or get() validates and returns the finished object. Eloquent queries work this way.',
      },
      {
        label: 'A trap',
        text: 'A fluent API alone is not a Builder. If the chain mutates the thing you already hold and never yields a separate result, it is only a fluent setter.',
      },
    ],
    whenToUse:
      'An object has many optional parts or must be assembled in steps, and you want a readable call site and one validation point.',
    whenNotTo:
      'The object has two or three required fields. A constructor or a Go struct literal is shorter and clearer than a builder.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'User::where("a", 1)->orderBy("id")->get() is Builder: steps first, result at get().',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'The query is assembled step by step and only produced when get() is called.',
      },
      {
        format: 'quick_pick',
        prompt: 'Which problem is Builder aimed at?',
        choices: [
          { label: 'Many optional parts, messy new', correct: true },
          { label: 'Many classes to pick between' },
          { label: 'A foreign API with the wrong shape' },
          { label: 'Too many global instances' },
        ],
        explanation: 'It replaces a long telescoping constructor with named, ordered steps.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `req := NewRequestBuilder("POST", url).
	Header("X-Id", id).
	Body(payload).
	Timeout(5 * time.Second)

r, err := req.Build()
if err != nil {
	return err
}`,
        choices: [
          { label: 'None of these' },
          { label: 'Prototype' },
          { label: 'Misused' },
          { label: 'Builder', correct: true },
        ],
        explanation: 'Steps are chained, then Build() validates and returns the finished request.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class Mail
{
    public function to($a)
    { $this->to = $a; return $this; }
    public function subject($s)
    { $this->s = $s; return $this; }
    public function send() { /* ... */ }
}

(new Mail)->to($a)->subject($s)->send();`,
        choices: [
          { label: 'Builder' },
          { label: 'Prototype' },
          { label: 'None of these', correct: true },
          { label: 'Misused' },
        ],
        explanation: 'It is a fluent setter on the object itself; no separate product is ever built.',
      },
    ],
  },
  {
    slug: 'prototype',
    teach: [
      {
        label: 'The problem',
        text: 'Duplicating a post with its tags and settings by re-creating each field is long and breaks when a column is added.',
      },
      {
        label: 'The shape',
        text: 'The object knows how to copy itself. Eloquent replicate() returns a new unsaved model with the same attributes, ready to tweak.',
      },
      {
        label: 'A trap',
        text: 'A shallow copy shares nested objects with the original. Change a nested slice or array element in the clone and the original changes too.',
      },
    ],
    whenToUse:
      'A new object is mostly like an existing one, such as a template or a duplicated record, and building it from scratch is costly or tedious.',
    whenNotTo:
      'The object is cheap to build or holds live resources such as connections and handles that must not be shared by a copy.',
    quizzes: [
      {
        format: 'judge',
        prompt: '$copy = $post->replicate(); $copy->title = "Copy"; is Prototype.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'A new object is made by copying an existing one, then adjusted.',
      },
      {
        format: 'quick_pick',
        prompt: 'What bug does a shallow copy often cause?',
        choices: [
          { label: 'Clone shares nested data', correct: true },
          { label: 'The clone has no fields at all' },
          { label: 'Two singletons exist' },
          { label: 'Callers learn the concrete class' },
        ],
        explanation: 'Only the top level is duplicated; nested references still point to the same data.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `func (t Template) Clone() Template {
	c := t
	c.Tags = append([]string(nil), t.Tags...)
	return c
}

doc := base.Clone()
doc.Title = "Weekly report"`,
        choices: [
          { label: 'Prototype', correct: true },
          { label: 'Builder' },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'The new document starts as a copy of base, with the slice copied deeply.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `func (t Template) Clone() Template {
	return t
}

a := base.Clone()
b := base.Clone()
a.Tags = append(a.Tags, "x")
b.Tags[0] = "y"`,
        choices: [
          { label: 'Prototype' },
          { label: 'Singleton' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation: 'The Tags slice is shared, so editing b can alter base and a: a shallow clone.',
      },
    ],
  },
  {
    slug: 'singleton',
    teach: [
      {
        label: 'The problem',
        text: 'Some things should exist once: a config, a connection pool, a logger. Two of them waste resources or disagree with each other.',
      },
      {
        label: 'The shape',
        text: 'In Laravel the container holds the single instance: $app->singleton(Foo::class, ...). Everyone asks the container and receives the same object.',
      },
      {
        label: 'A trap',
        text: 'The classic static getInstance() is global state in disguise. It hides dependencies and makes tests share leftovers. Prefer the container.',
      },
    ],
    whenToUse:
      'One shared instance is genuinely required, such as a connection pool, and the container can own it and hand it out.',
    whenNotTo:
      'You only want easy access from anywhere, or the object keeps mutable request-specific state. That is a global variable.',
    quizzes: [
      {
        format: 'judge',
        prompt: '$app->singleton(Clock::class, fn () => new Clock) is a container-managed Singleton.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'The container creates the instance once and returns it on every resolve.',
      },
      {
        format: 'quick_pick',
        prompt: 'Why is a static getInstance() considered risky?',
        choices: [
          { label: 'It is hidden global state', correct: true },
          { label: 'It creates two objects' },
          { label: 'It copies the object' },
          { label: 'It needs a factory' },
        ],
        explanation: 'Any code can reach it, so dependencies are invisible and tests leak state.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class Cart
{
    private static ?Cart $i = null;
    public array $items = [];

    public static function get(): Cart
    {
        return self::$i ??= new Cart();
    }
}`,
        choices: [
          { label: 'Singleton' },
          { label: 'None of these' },
          { label: 'Prototype' },
          { label: 'Misused', correct: true },
        ],
        explanation:
          'A singleton holding mutable per-user items is shared state that leaks between requests.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `var (
	once sync.Once
	pool *sql.DB
)

func DB() *sql.DB {
	once.Do(func() {
		pool, _ = sql.Open("pgx", dsn)
	})
	return pool
}`,
        choices: [
          { label: 'Misused' },
          { label: 'Builder' },
          { label: 'Singleton', correct: true },
          { label: 'None of these' },
        ],
        explanation: 'sync.Once guarantees one pool, created lazily and shared by all callers.',
      },
    ],
  },
  {
    slug: 'adapter',
    teach: [
      {
        label: 'The problem',
        text: 'Your code expects Storage with put() and get(), but the S3 SDK offers putObject() and getObject(). You cannot edit the SDK.',
      },
      {
        label: 'The shape',
        text: 'Write a small class that implements your interface and calls the foreign one inside. Flysystem adapters and ports-and-adapters code are this.',
      },
      {
        label: 'A trap',
        text: 'An adapter translates; it should not add behaviour. If the wrapper starts adding caching or retries, it has become something else.',
      },
    ],
    whenToUse:
      'You must use a class or service whose interface differs from the one your code expects, and you cannot or should not change it.',
    whenNotTo:
      'You own both sides. Change the interface directly, since a translation layer between two things you control is just extra code.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'A class implementing Storage by calling an S3 client inside is an Adapter.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'It converts the foreign SDK interface into the one your code already uses.',
      },
      {
        format: 'judge',
        prompt: 'This is an Adapter: it implements Logger, calls the real Logger and adds a timestamp.',
        choices: [{ label: 'True' }, { label: 'False', correct: true }],
        explanation: 'Same interface in and out, with added behaviour: that is a Decorator.',
      },
      {
        format: 'quick_pick',
        prompt: 'In an Adapter, which part is the adaptee?',
        choices: [
          { label: 'The foreign class being wrapped', correct: true },
          { label: 'The interface your code expects' },
          { label: 'The caller using the interface' },
          { label: 'The factory that builds it' },
        ],
        explanation: 'The adaptee is the existing class with the wrong interface; the adapter wraps it.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `type S3 struct{ c *s3.Client }

func (d S3) Put(k string, b []byte) error {
	_, err := d.c.PutObject(ctx(), &s3.In{
		Key:  &k,
		Body: bytes.NewReader(b),
	})
	return err
}`,
        choices: [
          { label: 'Bridge' },
          { label: 'Adapter', correct: true },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'S3Disk gives the SDK the Put signature the app expects.',
      },
    ],
  },
  {
    slug: 'bridge',
    teach: [
      {
        label: 'The problem',
        text: 'Mail types (welcome, receipt) times transports (SMTP, SES) gives a class for every pair. Each new type or transport multiplies the count.',
      },
      {
        label: 'The shape',
        text: 'Keep two hierarchies and link them by composition: a Mailable holds a Transport. Each side now varies without touching the other.',
      },
      {
        label: 'A trap',
        text: 'Bridge is planned up front for two axes of change. Adapter is a fix afterwards for classes that already exist and do not fit.',
      },
    ],
    whenToUse:
      'A thing varies along two independent axes, such as message kind and delivery channel, and subclassing every pair would explode.',
    whenNotTo:
      'Only one axis really varies. A plain interface and its implementations is enough; the extra layer adds indirection for nothing.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'A Mailable that holds a swappable Transport and delegates sending to it is a Bridge.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'Message kinds and delivery channels vary separately, joined by composition.',
      },
      {
        format: 'quick_pick',
        prompt: 'What does Bridge avoid?',
        choices: [
          { label: 'A subclass for every combination', correct: true },
          { label: 'Constructors with many arguments' },
          { label: 'More than one instance' },
          { label: 'Walking a tree of objects' },
        ],
        explanation: 'Two axes are separated, so two small hierarchies replace one product-sized one.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `type Sender interface{ Send(msg string) }

type Alert struct{ out Sender }

func (a Alert) Fire(m string) {
	a.out.Send("ALERT: " + m)
}

alert := Alert{out: SlackSender{}}`,
        choices: [
          { label: 'Adapter' },
          { label: 'Bridge', correct: true },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'Alert (the abstraction) delegates to a swappable Sender (the implementation).',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class WelcomeSmtpMail extends Mail {}
class WelcomeSesMail extends Mail {}
class ReceiptSmtpMail extends Mail {}
class ReceiptSesMail extends Mail {}

// next: InvoiceSmtpMail, InvoiceSesMail,
// then a Mailgun transport: 3 more...`,
        choices: [
          { label: 'Bridge' },
          { label: 'Composite' },
          { label: 'None of these' },
          { label: 'Misused', correct: true },
        ],
        explanation: 'A class per type-and-transport pair is the explosion Bridge exists to prevent.',
      },
    ],
  },
  {
    slug: 'composite',
    teach: [
      {
        label: 'The problem',
        text: 'A menu has links and submenus that hold more links. Code that asks "is this a link or a group?" everywhere gets messy fast.',
      },
      {
        label: 'The shape',
        text: 'Leaves and groups share one interface. A group holds children of that interface and forwards calls, so callers treat both alike.',
      },
      {
        label: 'A trap',
        text: 'Calls that make sense only for a group, such as add(), tempt you to put them on the shared interface. Leaves then need empty or throwing versions.',
      },
    ],
    whenToUse:
      'Data forms a tree of parts and wholes, such as menus, org units or nested components, and callers should not care which node they hold.',
    whenNotTo:
      'The structure is flat, or leaves and groups behave so differently that a shared interface would be mostly empty methods.',
    quizzes: [
      {
        format: 'judge',
        prompt: 'A Menu whose items can be a Link or another Menu, both with render(), is a Composite.',
        choices: [{ label: 'True', correct: true }, { label: 'False' }],
        explanation: 'Leaf and group share render(), so callers treat a single link and a tree alike.',
      },
      {
        format: 'quick_pick',
        prompt: 'In a Composite, what does a group do when asked to render()?',
        choices: [
          { label: 'Asks each child to render', correct: true },
          { label: 'Copies itself first' },
          { label: 'Picks one concrete class' },
          { label: 'Translates for a foreign API' },
        ],
        explanation: 'The group forwards the same call to its children, which may be groups too.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `type Node interface{ Size() int }

type File struct{ n int }

func (f File) Size() int { return f.n }

type Dir struct{ kids []Node }

func (d Dir) Size() (s int) {
	for _, k := range d.kids {
		s += k.Size()
	}
	return
}`,
        choices: [
          { label: 'Composite', correct: true },
          { label: 'Bridge' },
          { label: 'None of these' },
          { label: 'Misused' },
        ],
        explanation: 'Dir and File share Node; Dir sums its children through the same call.',
      },
      {
        format: 'spot',
        prompt: 'Which pattern is this?',
        code: `class Team
{
    /** @var Person[] */
    public array $people = [];

    public function names(): array
    {
        return array_column($this->people,
            'name');
    }
}`,
        choices: [
          { label: 'Composite' },
          { label: 'Adapter' },
          { label: 'None of these', correct: true },
          { label: 'Misused' },
        ],
        explanation: 'Team holds plain people, not nodes with a shared interface: a collection, not a tree.',
      },
    ],
  },
]
